import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { CheckCircle2 } from 'lucide-react';

import { authStateAtom } from '@/stores/auth';
import { LoginForm } from '@/components/auth/LoginForm';
import { ModeToggle } from '@/components/mode-toggle';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

const HIGHLIGHTS = [
  'AI-powered job scraping and extraction',
  'Automatic duplicate detection',
  'Team performance and customer insights',
];

function LoginPage() {
  const { isAuthenticated } = useAtomValue(authStateAtom);

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" />;
  }

  return (
    <div className="relative flex min-h-screen">
      {/* Theme toggle — available on the auth page too */}
      <div className="absolute right-4 top-4 z-20">
        <ModeToggle />
      </div>

      {/* Brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-primary p-12 text-primary-foreground lg:flex">
        {/* Soft decorative glows */}
        <div className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 size-80 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex items-center gap-3">
          <img src="/oubo-logo.webp" alt="Oubo" className="size-10 rounded-lg bg-white p-1 shadow-sm" />
          <span className="text-xl font-semibold">Oubo</span>
        </div>

        <div className="relative space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold leading-tight tracking-tight">
              Welcome back to
              <br />
              the Oubo admin
            </h1>
            <p className="max-w-md text-lg text-primary-foreground/80">
              Manage jobs, customers and your team from a single dashboard.
            </p>
          </div>
          <ul className="space-y-3">
            {HIGHLIGHTS.map((item) => (
              <li key={item} className="flex items-center gap-3 text-primary-foreground/90">
                <CheckCircle2 className="size-5 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-sm text-primary-foreground/70">
          © {new Date().getFullYear()} Oubo. All rights reserved.
        </p>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center bg-background px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm duration-500 animate-in fade-in slide-in-from-bottom-2">
          {/* Logo shown on small screens where the brand panel is hidden */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <img src="/oubo-logo.webp" alt="Oubo" className="size-9 rounded-lg" />
            <span className="text-lg font-semibold">Oubo</span>
          </div>

          <div className="mb-6 space-y-1.5">
            <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground">
              Enter your credentials to access the admin dashboard.
            </p>
          </div>

          <LoginForm />
        </div>
      </div>
    </div>
  );
}
